import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import mixin from 'react-mixin';

import GridFilter from './grid_filter';
import GridTable from './grid_table';
import GridPagination from './grid_pagination';
import GridActionsMixin from '../../mixins/grid_actions_mixin';
import PaginationHandler from '../../handlers/pagination_handler';
import SortingHandler from '../../handlers/sorting_handler';

import { forwardPropsToState } from 'utils/react_utils';
import { generateUUID } from 'utils/uuid';
import { FormAPI } from 'lecom-api';

@mixin.decorate(window.CssClassMixin)
@mixin.decorate(window.RequestHandlerMixin)
@mixin.decorate(window.RestActionsMixin)
@mixin.decorate(GridActionsMixin)
@forwardPropsToState([
	{prop: 'data', state: 'dataRows', propPath: 'data.dataRows'},
	{prop: 'data', state: 'count', propPath: 'data.count', defaultValue: 0},
	{prop: 'isLoading', state: 'gridIsLoading', defaultValue: false},
	{prop: 'selectedRowIds'},
	{prop: 'page'},
])
export default class Grid extends React.Component {
	static propTypes = {
		clientSide: PropTypes.bool,
		handleClientSidePagination: PropTypes.bool,
		url: PropTypes.string,
		eagerLoad: PropTypes.bool,
		resource: PropTypes.string,
		paginationConfigs: PropTypes.object,
		sortConfigs: PropTypes.object,
		sortData: PropTypes.object,
		filter: PropTypes.object,
		columns: PropTypes.object,
		data: PropTypes.object,
		emptyMessage: Realize.PropTypes.localizedString,
		dataRowsParam: PropTypes.string,
		countParam: PropTypes.string,
		selectedRowIdsParam: PropTypes.string,
		dataRowIdField: PropTypes.string,
		isLoading: PropTypes.bool,
		selectable: PropTypes.oneOf(['multiple', 'none', 'one']),
		tableClassName: PropTypes.string,
		onLoadSuccess: PropTypes.func,
		onLoadError: PropTypes.func,
		rowSelectableFilter: PropTypes.func,
		customTableHeader: PropTypes.string,
		forceShowSelectAllButton: PropTypes.bool,
		onClickRow: PropTypes.func,
		tableRowCssClass: PropTypes.func,
		paginationOnTop: PropTypes.bool,
		clearThemeTable: PropTypes.bool,
		pagination: PropTypes.bool,
		page: PropTypes.number,
		perPage: PropTypes.number,
		perPageOptions: PropTypes.array,
		onFilterSubmit:  PropTypes.func,
		onSelectDataRow: PropTypes.func,
		onRemoveSelection: PropTypes.func,
		onSelectAllRows: PropTypes.func,
		onPagination: PropTypes.func,
		isMobile: PropTypes.bool,
		typeScreen: PropTypes.string
	};

	static defaultProps = {
		gridTableComponent: GridTable,
		url: '',
		clientSide: false,
		handleClientSidePagination: true,
		themeClassKey: 'grid',
		eagerLoad: false,
		paginationConfigs: {},
		sortConfigs: {},
		sortData: {},
		filter: {},
		columns: {},
		dataRowsParam: 'data',
		countParam: 'count',
		selectedRowIdsParam: 'rowIds',
		dataRowIdField: 'id',
		isLoading: false,
		selectable: 'multiple',
		rowSelectableFilter: null,
		customTableHeader: null,
		forceShowSelectAllButton: false,
		onClickRow: null,
		tableRowCssClass: null,
		paginationOnTop: true,
		clearThemeTable: false,
		pagination: true,
		page: 1,
		perPage: null,
		onLoadSuccess: function(data) {},
		onLoadError: function(xhr, status, error) {},
		onFilterSubmit: function(event, postData) {},
		onSelectDataRow: function(event, selectedRowIds) {},
		onRemoveSelection: function(event) {},
		onSelectAllRows: function(event) {},
		onPagination: function() {},
		isMobile: false,
		data: {
			dataRows: [],
			count: 0
		}
	};

	state = {
		dataRows: this.props.data.dataRows,
		selectedData: {},
		selectedRowIds: [],
		allSelected: false,
		count: this.props.data.count,
		page: this.props.page,
		perPage: this.getInitialPerPage(),
		filterData: {},
		sortData: this.props.sortData,
		gridIsLoading: this.props.isLoading
	};

	filterColumns = {}

	constructor (props) {
		super(props);

		// Filter event listeners
		this.onFilterSubmit = this.onFilterSubmit.bind(this);

		// Table event listeners
		this.onSort = this.onSort.bind(this);
		this.selectDataRows = this.selectDataRows.bind(this);
		this.removeSelection = this.removeSelection.bind(this);
		this.selectAllRows = this.selectAllRows.bind(this);

		// Pagination event listeners
		this.onPagination = this.onPagination.bind(this);
		this.onChangePerPage = this.onChangePerPage.bind(this);

		// Server-side data load handlers
		this.handleLoad = this.handleLoad.bind(this);
		this.handleLoadError = this.handleLoadError.bind(this);
	}

	componentDidMount () {
		this.setState({
			filterData: this.getInitialFilterData()
		}, function() {
			if(!!this.props.eagerLoad) {
				this.loadData();
			}
		}.bind(this));
	}

	renderPaginationOnTop () {
		return (this.props.paginationOnTop)
		? this.renderPagination()
		: <span />;
	}

	renderFilter () {
		if (this.props.filter && !Object.keys(this.props.filter).length) {
			return '';
		}

		return (
			<GridFilter
			action={this.props.url}
			{...this.props.filter}
			isLoading={this.state.gridIsLoading}
			onSubmit={this.onFilterSubmit}
			ref="filter"
			/>
		);
	}

	renderTable () {
		const { gridTableComponent: GridTableComponent } = this.props;
				
		return (
			<div className="container-gridtable">
				<GridTableComponent
					resource={this.props.resource}
					columns={this.props.columns}
					sortConfigs={this.sortConfigs()}
					sortData={this.state.sortData}
					dataRows={this.paginateDataRows()}
					selectable={this.props.selectable}
					selectedRowIds={this.state.selectedRowIds}
					selectedRowIdsParam={this.props.selectedRowIdsParam}
					dataRowIdField={this.props.dataRowIdField}
					allSelected={this.state.allSelected}
					allSelectedData={this.state.filterData}
					count={this.state.count}
					actionButtons={this.getActionButtons.apply(this)}
					tableClassName={this.props.tableClassName}
					onSort={this.onSort}
					onSelect={this.selectDataRows}
					onRemoveSelection={this.removeSelection}
					onSelectAll={this.selectAllRows}
					rowSelectableFilter={this.props.rowSelectableFilter}
					customTableHeader={this.props.customTableHeader}
					forceShowSelectAllButton={this.props.forceShowSelectAllButton}
					onClickRow={this.props.onClickRow}
					rowHref={this.getRowHref()}
					tableRowCssClass={this.props.tableRowCssClass}
					clearThemeTable={this.props.clearThemeTable}
					emptyMessage={this.props.emptyMessage}
					gridId={this.props.id}
					contextType={this.props.contextType}
					readOnly={this.props.readOnly}
					disabled={this.props.disabled}
					onSortTable={this.sortTable.bind(this)}
					filterClientSideTable={this.filterTable.bind(this)}
					isMobile={this.props.isMobile}
					fields={this.props.fields ? this.props.fields : this.props.filter.inputs}
					typeScreen={this.props.typeScreen}
				/>
			</div>
		);
	}

	renderPagination () {
		if (this.props.pagination) {
			return (
				<GridPagination
					{...this.paginationConfigs()}
					className="paginacao-grid"
					perPage={this.state.perPage}
					page={this.state.page}
					count={this.state.count}
					onPagination={this.onPagination}
					onChangePerPage={this.onChangePerPage}
					pageRowsCount={this.paginateDataRows().length}
					key={generateUUID()}
				/>
			);
		}
	}

	render() {
		return (
			<div className={this.gridClassName()} ref="grid">
				{this.renderFilter()}

				{/* {this.renderPaginationOnTop()} */}
				{this.renderTable()}
				{this.renderPagination()}
			</div>
		);
	}

	/* Initializers and configs */

	getInitialPerPage() {
		return (this.props.perPage || this.paginationConfigs().perPage);
	}

	getInitialFilterData () {
		let gridFilterNode = ReactDOM.findDOMNode(this.refs.filter);
		let filterForm = $(gridFilterNode).find('form');

		return filterForm.serializeObject();
	}

	gridClassName() {
		let className = this.className();
		if(this.state.gridIsLoading) {
			className += ' loading';
		}

		return className;
	}

	paginationConfigs() {
		return Object.assign({}, Realize.config.grid.pagination, this.props.paginationConfigs);
	}

	sortConfigs() {
		return Object.assign({}, Realize.config.grid.sort, this.props.sortConfigs);
	}

	focusPagination(){
		let paginationVisible = $('.pagination span li').length > 0 ? true : false;

		if(paginationVisible === true){
			let pagination = $('.pagination span li');
			let paginationAnchor = $('.pagination span li a');

			pagination.attr('tabindex', '0');
			paginationAnchor.attr('tabindex', '-1');

			pagination.keypress((e) => {
				if(e.which === 13 || e.keyCode === 13){
					e.currentTarget.click();
				}
			});

			let elementActive = $(`#input__${this.props.id} .pagination li.active`);
			elementActive.focus();
		}
	}

	/* Event listeners */

	onPagination = (page) => {
		this.state.page = page;

		if(this.state.allSelected) {
			this.state.selectedRowIds = [];
		}

		this.state.allSelected = false;
		if(this.props.clientSide){
			setTimeout(() => { this.focusPagination() }, 200);
			this.forceUpdate(this.props.onPagination.bind(this, this.state));
		}
		else {
			this.loadData();
		}
	};

	onChangePerPage = (perPage) => {
		this.state.perPage = perPage;
		this.onPagination(1);
	};

	onFilterSubmit = (event, postData) => {
		this.props.onFilterSubmit(event, postData);

		if(!event.isDefaultPrevented()) {
			event.preventDefault();

			this.state.selectedRowIds = [];
			this.state.allSelected = false;
			this.state.filterData = postData;
			this.state.page = 1;
			this.loadData();
		}
	};

	onSort = (sortData) => {
		this.state.sortData = sortData;
		this.state.page = 1;
		this.sort();
	};

	/* Pagination handlers */

	paginateDataRows() {
		return this.props.handleClientSidePagination
		? PaginationHandler.paginate(this.state)
		: this.state.dataRows;
	}

	/* Sorting handlers */

	sort() {
		if(!!this.props.clientSide) {
			this.sortClientSide();
		} else {
			this.loadData();
		}
	}

	sortClientSide() {
		const sortData = this.buildClientSideSortData();
		const { dataRows } = this.state;
		const sortedDataRows = SortingHandler.sort(dataRows, sortData);

		this.setState({
			dataRows: sortedDataRows,
			gridIsLoading: false
		});
	}

	/**
	 * @description Realiza filtro da tabela pelo input da coluna
	 * @param {String} nameColumn Nome da coluna
	 * @param {String} valueInputTableHeader Valor do input
	 */
	filterTable(nameColumn, valueInputTableHeader) {
		let tableFiltered = [...this.props.data.dataRows]

		this.filterColumns[nameColumn] = valueInputTableHeader

		Object.keys(this.filterColumns).forEach(key => {
			tableFiltered = tableFiltered.filter(row => {
				if(!row[key]) {
					row[key] = ''
				}
				return row[key]
					.toString()
					.toLowerCase()
					.indexOf(this.filterColumns[key].toLowerCase()) !== -1
			})
		})
		this.setState({dataRows: tableFiltered})
	}

	/**
	 * @description Realiza ordenação crescente ou decrescente pelo nome da coluna
	 * @param {String} nameColumn Nome da coluna a ser ordenada
	 * @param {String} typeOrdination Tipo da ordenação (ASC ou DESC)
	 */
	sortTable (nameColumn, typeOrdination) {
		let tableOrdered = [...this.state.dataRows]
		let firstReturnSort = 0
		let secondReturnSort = 0

		if(typeOrdination === 'ASC') {
			firstReturnSort = -1
			secondReturnSort = 1
		}

		if(typeOrdination === 'DESC') {
			firstReturnSort = 1
			secondReturnSort = -1
		}

		tableOrdered.sort((nextRow, currentRow) => (
			nextRow[nameColumn] < currentRow[nameColumn] ? firstReturnSort
			:
			nextRow[nameColumn] > currentRow[nameColumn] ? secondReturnSort
			: 0
		))

		this.setState({dataRows: tableOrdered})
	}

	buildClientSideSortData() {
		let baseSortData = this.state.sortData;
		let columns = this.props.columns;

		return Object.assign({}, baseSortData, columns[baseSortData.field]);
	}

	/* Server-side data handlers */

	loadData() {
		this.setState({gridIsLoading: true});
		let postData = this.buildPostData();
		let filterProps = this.props.filter;
		let filterMethod = filterProps.method || 'GET';
		let filterDataType = filterProps.dataType || 'json';

		$.ajax({
			url: this.getRestActionUrl('index'),
			method: filterMethod,
			dataType: filterDataType,
			data: postData,
			success: this.handleLoad,
			error: this.handleLoadError
		});
	}

	handleLoad = (data) => {
		let dataRows = Realize.utils.getProp(this.props.dataRowsParam, data);
		let count = Realize.utils.getProp(this.props.countParam, data);

		this.setState({
			gridIsLoading: false,
			dataRows: dataRows,
			count: count
		}, function() {
			this.props.onLoadSuccess(data);
		}.bind(this));
	};

	handleLoadError = (xhr, status, error) => {
		this.props.onLoadError(xhr, status, error);
		this.setState({gridIsLoading: false});
		console.log('Grid Load Error:' + error);
	};

	buildPostData () {
		var postData = $.extend({}, this.state.filterData);

		$.extend(postData, this.buildPaginationPostData());
		if(!$.isEmptyObject(this.state.sortData)) {
			$.extend(postData, this.buildSortPostData());
		}

		return postData;
	}

	buildPaginationPostData () {
		var paginationPostData = {};

		var paginationParam = this.paginationConfigs().param;
		var paginationParamPerPage = 'per_page';

		paginationPostData[paginationParam] = this.state.page;
		paginationPostData[paginationParamPerPage] = this.state.perPage;

		return paginationPostData;
	}

	buildSortPostData () {
		var sortConfigs = this.sortConfigs();

		var sortParam = sortConfigs.param;
		var sortDirectionParam = sortConfigs.directionParam;
		var sortPostData = {};
		sortPostData[sortParam] = this.parseSortPostDataValue();
		sortPostData[sortDirectionParam] = this.state.sortData.direction;

		return sortPostData;
	}

	parseSortPostDataValue () {
		var sortValueFormat = this.sortConfigs().fieldValueFormat;
		var field = this.state.sortData.field;
		var direction = this.state.sortData.direction;

		if(!sortValueFormat) {
			return field;
		}

		return sortValueFormat.replace(/%\{field}/, field).replace(/%\{direction}/, direction);
	}

	/* Selection handlers */

	selectDataRows = (event, selectedRowIds, selectedData) => {
		this.props.onSelectDataRow(event, selectedRowIds, selectedData);
		if(!event.isDefaultPrevented()) {
			event.preventDefault();

			var nextState = this.getSelectDataRowsState(selectedRowIds, selectedData);
			this.setState(nextState);
		}
	};

	getSelectDataRowsState = (selectedRowIds, selectedData) => {
		let nextState = {selectedRowIds: selectedRowIds, allSelected: false};

		if (!!selectedData && this.props.selectable === 'one')
			nextState.selectedData = selectedData;

		return nextState;
	};

	removeSelection = (event) => {
		this.props.onRemoveSelection(event);
		if(!event.isDefaultPrevented()) {
			event.preventDefault();

			this.setState({
				selectedRowIds: [],
				allSelected: false
			});
		}
	};

	selectAllRows = (event) => {
		this.props.onSelectAllRows(event);
		if(!event.isDefaultPrevented()) {
			event.preventDefault();

			this.setState({
				allSelected: true
			});
		}
	};

	serialize () {
		return this.state.dataRows;
	}
}
